/**
 * "Do this in Dentrix": the one pattern for a change Velora keeps but cannot
 * write to the practice software yet (the write-back command is gated, see
 * WRITE_READY in @velora/domain/frontdesk, or no connector command exists).
 *
 * The API says what staff do in the PMS and why Velora did not write it; the
 * web shows it the same way everywhere (`<DoInPms>`). Nothing here sends
 * anything.
 */
import { z } from 'zod';

export const PMS_TODO_SYSTEMS = ['dentrix', 'opendental', 'eaglesoft'] as const;

export const DoInPmsSchema = z.object({
  pmsSystem: z.enum(PMS_TODO_SYSTEMS),
  /** "Dentrix", "Open Dental", "Eaglesoft". */
  pmsName: z.string(),
  /** What to do there, one imperative sentence ("Set the case status to Accepted."). */
  action: z.string(),
  /** Where in the PMS ("Treatment Planner", "Lab Case Manager"), when known. */
  where: z.string().nullable(),
  /** Why Velora did not write it (the gate's reason), when known. */
  reason: z.string().nullable(),
});
export type DoInPms = z.infer<typeof DoInPmsSchema>;

const NAMES: Readonly<Record<(typeof PMS_TODO_SYSTEMS)[number], string>> = {
  dentrix: 'Dentrix',
  opendental: 'Open Dental',
  eaglesoft: 'Eaglesoft',
};

export function pmsTodoName(system: string | null | undefined): string {
  return (system && NAMES[system as keyof typeof NAMES]) || 'the PMS';
}

/** Builds the note; null when there is no PMS (Velora-only office). */
export function doInPms(
  system: string | null | undefined,
  action: string,
  where: string | null = null,
  reason: string | null = null,
): DoInPms | null {
  if (system !== 'dentrix' && system !== 'opendental' && system !== 'eaglesoft') return null;
  return { pmsSystem: system, pmsName: NAMES[system], action, where, reason };
}
